"use client"

import { useState } from "react"
import { Loader2 } from "lucide-react"
import axios from "axios"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { resolveApiBase } from "@/lib/lower/api-base"
import { cn } from "@/lib/utils"

type RequestAccessDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function RequestAccessDialog({ open, onOpenChange }: RequestAccessDialogProps) {
  const [name, setName] = useState("")
  const [email, setEmail] = useState("")
  const [company, setCompany] = useState("")
  const [message, setMessage] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const resetForm = () => {
    setName("")
    setEmail("")
    setCompany("")
    setMessage("")
    setError(null)
    setSuccess(false)
  }

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      resetForm()
    }
    onOpenChange(next)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setIsLoading(true)

    try {
      await axios.post(`${resolveApiBase()}/api/public/access-request`, {
        name: name.trim(),
        email: email.trim(),
        company: company.trim() || null,
        message: message.trim() || null,
      })
      setSuccess(true)
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.data?.error) {
        setError(String(err.response.data.error))
      } else {
        setError("Не удалось отправить заявку. Попробуйте позже или напишите на hello@yurgo.ru")
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        {success ? (
          <>
            <DialogHeader>
              <DialogTitle>Заявка отправлена</DialogTitle>
              <DialogDescription>
                Спасибо! Мы свяжемся с вами по указанной эл. почте в ближайшее время.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button onClick={() => handleOpenChange(false)}>Закрыть</Button>
            </DialogFooter>
          </>
        ) : (
          <form onSubmit={handleSubmit}>
            <DialogHeader>
              <DialogTitle>Запросить доступ</DialogTitle>
              <DialogDescription>
                Оставьте контакты — пришлём приглашение на пилот или демо ЮРГО.
              </DialogDescription>
            </DialogHeader>

            <div className="mt-4 space-y-4">
              {error && (
                <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="access-name">Имя</Label>
                <Input
                  id="access-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Иван Иванов"
                  required
                  autoComplete="name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="access-email">Эл. почта</Label>
                <Input
                  id="access-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="name@company.ru"
                  required
                  autoComplete="email"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="access-company">Компания</Label>
                <Input
                  id="access-company"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  placeholder="ООО «Пример»"
                  autoComplete="organization"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="access-message">Комментарий</Label>
                <Textarea
                  id="access-message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Кратко опишите задачу или тип дел"
                  rows={3}
                />
              </div>
            </div>

            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
                Отмена
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="size-4 animate-spin" />
                    Отправка…
                  </>
                ) : (
                  "Отправить заявку"
                )}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}

type RequestAccessButtonProps = React.ComponentProps<typeof Button> & {
  label?: string
}

export function RequestAccessButton({
  label = "Запросить доступ",
  className,
  children,
  ...props
}: RequestAccessButtonProps) {
  const [open, setOpen] = useState(false)

  return (
    <>
      <Button
        type="button"
        className={cn(className)}
        onClick={() => setOpen(true)}
        {...props}
      >
        {children ?? label}
      </Button>
      <RequestAccessDialog open={open} onOpenChange={setOpen} />
    </>
  )
}
